/**
 * Source-text cleanup for the Spanish edition (PLACSP feeds), shared by the API
 * adapter (where a row is written) and the web (where an already-stored row is
 * shown, until the backfill in `services/api/scripts/` has run).
 *
 * Every function here is pure and deterministic — no `Intl`, no clock — so the
 * SSR render, the prerender and the browser print the same bytes.
 */

/**
 * HTML entities that PLACSP bodies leave in plain-text CODICE fields. The XML
 * parser decodes the five XML entities once; an editor that pasted HTML into a
 * title leaves `&nbsp;` behind (and `&amp;nbsp;` when it was escaped twice), which
 * the page then printed verbatim — 348 stored titles, 40 of them live (2026-10-21).
 * Only names in this table are decoded, so a real «A&B» or «&c» stays as written.
 */
const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  nbsp: '\u00A0',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  shy: '',
  aacute: 'á',
  eacute: 'é',
  iacute: 'í',
  oacute: 'ó',
  uacute: 'ú',
  Aacute: 'Á',
  Eacute: 'É',
  Iacute: 'Í',
  Oacute: 'Ó',
  Uacute: 'Ú',
  agrave: 'à',
  egrave: 'è',
  ograve: 'ò',
  Agrave: 'À',
  Egrave: 'È',
  Ograve: 'Ò',
  ntilde: 'ñ',
  Ntilde: 'Ñ',
  ccedil: 'ç',
  Ccedil: 'Ç',
  uuml: 'ü',
  Uuml: 'Ü',
  iuml: 'ï',
  Iuml: 'Ï',
  middot: '·',
  ordm: 'º',
  ordf: 'ª',
  deg: '°',
  euro: '€',
  laquo: '«',
  raquo: '»',
  ldquo: '“',
  rdquo: '”',
  lsquo: '‘',
  rsquo: '’',
  hellip: '…',
  ndash: '–',
  mdash: '—',
  bull: '•',
  iexcl: '¡',
  iquest: '¿',
  times: '×',
  sup2: '²',
  sup3: '³',
  frac12: '½',
};

const ENTITY = /&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|([A-Za-z][A-Za-z0-9]{1,8}));/g;

function decodeOnce(s: string): string {
  return s.replace(ENTITY, (whole, dec?: string, hex?: string, name?: string) => {
    if (name !== undefined) return NAMED_ENTITIES[name] ?? whole;
    const code = dec !== undefined ? Number(dec) : parseInt(hex ?? '', 16);
    // Control characters and surrogates are never text a reader should get.
    if (!Number.isFinite(code) || code < 0x20 || code > 0x10ffff) return whole;
    if (code >= 0xd800 && code <= 0xdfff) return whole;
    return String.fromCodePoint(code);
  });
}

/**
 * Decode the known HTML entities and numeric references, repeating while a pass
 * still changes something (at most three: `&amp;amp;nbsp;` is the worst seen in
 * the wild). Unknown names stay verbatim.
 */
export function decodeSourceEntities(s: string): string {
  let out = s;
  for (let i = 0; i < 3 && out.includes('&'); i += 1) {
    const next = decodeOnce(out);
    if (next === out) break;
    out = next;
  }
  return out;
}

/**
 * Text as a Spanish page prints it: entities decoded, the no-break spaces they
 * leave turned into plain ones and runs of spaces collapsed. Line breaks are
 * kept, so a multi-paragraph description keeps its paragraphs.
 */
export function cleanSourceText(s: string): string {
  if (!s.includes('&') && !s.includes('\u00A0')) return s;
  return decodeSourceEntities(s)
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +\n/g, '\n')
    .replace(/\n +/g, '\n')
    .trim();
}

const ES_MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

/** `2027-01-05` → «5 de enero de 2027»; anything else → null. */
export function spanishLongDate(isoDate: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${day} de ${ES_MONTHS[month - 1]} de ${m[1]}`;
}

/**
 * Every bare ISO date in a sentence spelled the Spanish way — «Del 2027-01-01 al
 * 2027-12-31» (the PLACSP contract term, 1,174 live lots on 2026-10-21) becomes
 * «Del 1 de enero de 2027 al 31 de diciembre de 2027». A timestamp
 * (`2027-01-01T…`) or a longer digit run is left alone. No lookbehind: the web
 * bundle imports this, and a lookbehind literal is a parse error on Safari < 16.4.
 */
export function isoDatesToSpanish(s: string): string {
  return s.replace(
    /(^|[^\d-])(\d{4}-\d{2}-\d{2})(?![\dT-])/g,
    (_w, pre: string, d: string) => `${pre}${spanishLongDate(d) ?? d}`,
  );
}

/**
 * A PLACSP lot id as a reader sees it: `004` → `4`. Non-numeric ids («A», «1.2»)
 * are returned as published.
 */
export function placspLotNumber(label: string): string {
  return /^\d+$/.test(label) ? String(Number(label)) : label;
}

/**
 * «Lote 4 de 6» — or «Lote 5» alone when the published lot id is larger than
 * the count (an expediente whose lots are numbered 004/005 but only three of
 * them are in the notice printed «Lote 005 de 3»), or when the count is unknown.
 */
export function placspLotOf(label: string, count: number | null | undefined): string {
  const n = placspLotNumber(label);
  const num = /^\d+$/.test(n) ? Number(n) : null;
  if (count == null || !Number.isFinite(count) || count < 1) return `Lote ${n}`;
  if (num != null && num > count) return `Lote ${n}`;
  return `Lote ${n} de ${count}`;
}

/**
 * The same rule applied to a sentence already stored by an older adapter
 * («Lote 005 de 3 del expediente …»). Only the «Lote N de M del expediente»
 * shape the adapter writes is touched.
 */
export function normalizePlacspLotOf(s: string): string {
  return s.replace(/\bLote (\d+) de (\d+)(?= del expediente)/g, (_w, n: string, m: string) =>
    placspLotOf(n, Number(m)),
  );
}
